<template>
  <div class="tests-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-tasks"></i></span> Tests
        </h1>
        <router-link to="/tests/create" class="btn btn-primary btn-lg btn-with-icon with-hover-effect animate-fade-in">
          <span class="icon me-2"><i class="fas fa-plus-circle"></i></span> Neuen Test erstellen
        </router-link>
      </div>

      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-with-table">
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h4 class="card-title mb-0">Alle Tests</h4>
                <div class="d-flex flex-wrap gap-2">
                  <div class="search-input-group">
                    <div class="input-group with-focus-effect">
                      <span class="input-group-text border-end-0 bg-transparent">
                        <span class="icon icon-primary"><i class="fas fa-search"></i></span>
                      </span>
                      <input 
                        type="text" 
                        class="form-control border-start-0" 
                        placeholder="Suche nach Tests..." 
                        v-model="searchQuery"
                      >
                    </div>
                  </div>
                  <div class="dropdown">
                    <button 
                      class="btn btn-outline-secondary btn-with-icon with-hover-effect dropdown-toggle" 
                      type="button" 
                      id="filterDropdown" 
                      data-bs-toggle="dropdown" 
                      aria-expanded="false"
                    >
                      <span class="icon me-1"><i class="fas fa-filter"></i></span> Filter
                    </button>
                    <ul class="dropdown-menu dropdown-menu-animated" aria-labelledby="filterDropdown">
                      <li>
                        <button class="dropdown-item dropdown-item-hover" @click="filterTests('all')">
                          <span class="icon icon-primary me-2"><i class="fas fa-list"></i></span> Alle Tests
                        </button>
                      </li>
                      <li>
                        <button class="dropdown-item dropdown-item-hover" @click="filterTests('active')">
                          <span class="icon icon-success me-2"><i class="fas fa-check-circle"></i></span> Nur aktive Tests
                        </button>
                      </li>
                      <li>
                        <button class="dropdown-item dropdown-item-hover" @click="filterTests('inactive')">
                          <span class="icon icon-danger me-2"><i class="fas fa-times-circle"></i></span> Nur inaktive Tests
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>

            <div v-if="loading" class="text-center py-5 content-loading">
              <div class="loader loader-lg mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Tests werden geladen...</p>
            </div>

            <div v-else-if="filteredTests.length === 0" class="text-center py-5 content-empty">
              <span class="icon icon-muted icon-xxl d-block mb-3"><i class="fas fa-clipboard-list"></i></span>
              <p class="lead text-muted mb-4">Keine Tests gefunden.</p>
              <router-link to="/tests/create" class="btn btn-outline-primary btn-with-icon with-hover-effect">
                <span class="icon me-2"><i class="fas fa-plus-circle"></i></span> Erstellen Sie Ihren ersten Test
              </router-link>
            </div>

            <div v-else class="test-list-container content-padded">
              <draggable 
                v-model="sortableTests" 
                handle=".drag-handle"
                ghost-class="draggable-ghost"
                animation="300"
                @end="saveTestsOrder"
                :disabled="currentFilter !== 'all'"
                class="card-list-sortable mb-0"
                item-key="id"
              >
                <template #item="{element}">
                  <div 
                    :key="element.id" 
                    class="card-list-item with-transition"
                    :class="{ 'card-list-item-inactive': !element.active }"
                  >
                    <div class="row align-items-center g-3">
                      <div class="col-auto drag-handle" v-if="currentFilter === 'all'">
                        <span class="icon icon-muted icon-lg cursor-grab"><i class="fas fa-grip-vertical"></i></span>
                      </div>
                      <div class="col">
                        <h5 class="item-title mb-1 fw-bold">{{ element.name }}</h5>
                        <div class="item-meta mb-1">
                          <span class="badge me-2" :class="element.active ? 'bg-success' : 'bg-danger'">
                            {{ element.active ? 'Aktiv' : 'Inaktiv' }}
                          </span>
                          <span class="item-meta-stat me-2">
                            <span class="icon me-1 small"><i class="fas fa-question-circle"></i></span>
                            {{ getQuestionCount(element) }} Fragen
                          </span>
                          <span class="item-meta-stat me-2">
                            <span class="icon me-1 small"><i class="fas fa-star"></i></span>
                            {{ getTotalPoints(element) }} Punkte
                          </span>
                          <span class="item-meta-stat">
                            <span class="icon me-1 small"><i class="fas fa-clock"></i></span>
                            Erstellt: {{ formatDate(element.created_at) }}
                          </span>
                        </div>
                        <p class="item-description mb-0 text-truncate">{{ element.description || 'Keine Beschreibung vorhanden.' }}</p>
                      </div>
                      <div class="col-auto">
                        <div class="action-buttons">
                          <router-link 
                            :to="`/tests/${element.id}/preview`" 
                            class="btn-icon btn-icon-primary with-tooltip" 
                            data-tooltip="Vorschau"
                          >
                            <i class="fas fa-eye"></i>
                          </router-link>
                          <button 
                            class="btn-icon btn-icon-info with-tooltip" 
                            @click="copyTestLink(element.hash)"
                            data-tooltip="Link kopieren"
                          >
                            <i class="fas fa-copy"></i>
                          </button>
                          <router-link 
                            :to="`/tests/${element.id}/edit`" 
                            class="btn-icon btn-icon-secondary with-tooltip" 
                            data-tooltip="Bearbeiten"
                          >
                            <i class="fas fa-edit"></i>
                          </router-link>
                          <button 
                            class="btn-icon btn-icon-danger with-tooltip" 
                            @click="confirmDeleteTest(element)"
                            data-tooltip="Löschen"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
            
            <template v-slot:footer v-if="filteredTests.length > 0 && currentFilter === 'all'">
              <div class="text-center text-muted">
                <p class="small mb-0">
                  <span class="icon me-1"><i class="fas fa-info-circle"></i></span> 
                  Ziehen Sie die Tests, um die Anzeigereihenfolge zu ändern
                </p>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </div>
    
    <!-- Share Modal -->
    <BModal 
      ref="shareModal"
      title="Test teilen"
      ok-title="Schließen"
      cancel-title=""
      centered
      class="modal-with-form"
    >
      <div class="modal-form">
        <div class="alert alert-info alert-with-icon mb-4">
          <span class="alert-icon"><i class="fas fa-info-circle"></i></span>
          <div>
            Teilen Sie diesen Link, um Teilnehmer zum Test einzuladen.
            Der Link ist öffentlich zugänglich - jeder mit dem Link kann den Test absolvieren.
          </div>
        </div>
        
        <div class="input-group input-group-lg with-focus-effect mb-3">
          <input 
            type="text" 
            class="form-control" 
            :value="currentTestUrl" 
            readonly
          >
          <button 
            class="btn btn-primary" 
            @click="copyTestLink(currentTestHash)"
          >
            <span class="icon"><i class="fas fa-copy"></i></span>
          </button>
        </div>
        
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <span class="badge" :class="currentTestActive ? 'bg-success' : 'bg-danger'">
              {{ currentTestActive ? 'Test ist aktiv' : 'Test ist inaktiv' }}
            </span>
          </div>
          <div>
            <router-link 
              :to="`/tests/${currentTestId}/edit`" 
              class="btn btn-sm btn-outline-secondary btn-with-icon with-hover-effect"
            >
              <span class="icon me-1"><i class="fas fa-edit"></i></span> 
              Test bearbeiten
            </router-link>
          </div>
        </div>
      </div>
    </BModal>
    
    <!-- Delete Modal -->
    <BModal
      ref="deleteModal"
      title="Test löschen"
      ok-title="Ja, löschen"
      cancel-title="Abbrechen"
      ok-variant="danger"
      @ok="deleteTest"
      centered
      class="modal-with-form"
    >
      <div v-if="testToDelete" class="modal-form">
        <div class="alert alert-danger alert-with-icon mb-4">
          <span class="alert-icon"><i class="fas fa-exclamation-triangle"></i></span>
          <div>
            Sind Sie sicher, dass Sie den Test <strong>{{ testToDelete.name }}</strong> löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </div>
        </div>
        
        <p>Alle mit diesem Test verbundenen Daten werden gelöscht, einschließlich:</p>
        <ul class="list-with-icons mb-0">
          <li><span class="icon icon-danger me-2"><i class="fas fa-question-circle"></i></span> Alle Fragen des Tests</li>
          <li><span class="icon icon-danger me-2"><i class="fas fa-clipboard-list"></i></span> Alle Testergebnisse</li>
          <li><span class="icon icon-danger me-2"><i class="fas fa-link"></i></span> Der Test-Link wird ungültig</li>
        </ul>
      </div>
    </BModal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useTestsStore } from '@/stores/tests';
import { useToast } from 'vue-toastification';
import Card from '@/components/ui/Card.vue';
import Draggable from 'vuedraggable';

export default defineComponent({
  name: 'TestsList',
  components: {
    Card,
    Draggable
  },
  setup() {
    const router = useRouter();
    const testsStore = useTestsStore();
    const toast = useToast();
    
    // Refs für Template
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentFilter = ref('all'); // 'all', 'active', 'inactive'
    const testToDelete = ref(null);
    const currentTestUrl = ref('');
    const currentTestHash = ref('');
    const currentTestId = ref(null);
    const currentTestActive = ref(false);
    const sortableTests = ref([]);
    const deleteModal = ref(null);
    const shareModal = ref(null);
    
    // Computed properties
    const tests = computed(() => {
      return testsStore.getAllTests || [];
    });
    
    const filteredTests = computed(() => {
      if (!Array.isArray(tests.value)) return [];
      
      let filtered = [...tests.value];
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(test => 
          (test.name && test.name.toLowerCase().includes(query)) || 
          (test.description && test.description.toLowerCase().includes(query))
        );
      }
      
      if (currentFilter.value === 'active') {
        filtered = filtered.filter(test => test.active);
      } else if (currentFilter.value === 'inactive') {
        filtered = filtered.filter(test => !test.active);
      }
      
      return filtered;
    });
    
    // Watch für filteredTests
    const updateSortableTests = () => {
      sortableTests.value = [...filteredTests.value];
    };
    
    // Methods
    const loadTests = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        await testsStore.fetchTests();
        console.log('Geladene Tests:', testsStore.getAllTests);
        updateSortableTests();
      } catch (err) {
        console.error('Fehler beim Laden der Tests:', err);
        error.value = 'Fehler beim Laden der Tests';
        toast.error(error.value);
      } finally {
        loading.value = false;
      }
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '—';
      
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    };
    
    const showShareModal = () => {
      if (shareModal.value) {
        shareModal.value.show();
      } else {
        console.warn('Share Modal konnte nicht angezeigt werden');
      }
    };

    const showDeleteModal = () => {
      if (deleteModal.value) {
        deleteModal.value.show();
      } else {
        console.warn('Delete Modal konnte nicht angezeigt werden');
      }
    };
    
    const shareTest = (test) => {
      currentTestUrl.value = `${window.location.origin}/test/${test.hash}`;
      currentTestHash.value = test.hash;
      currentTestId.value = test.id;
      currentTestActive.value = test.active;
      showShareModal();
    };
    
    const filterTests = (filter) => {
      currentFilter.value = filter;
      updateSortableTests();
    };
    
    const copyTestLink = (hash) => {
      const testUrl = `${window.location.origin}/test/${hash}`;
      navigator.clipboard.writeText(testUrl).then(() => {
        toast.success('Test-Link wurde in die Zwischenablage kopiert');
      }).catch((err) => {
        toast.error('Fehler beim Kopieren des Links');
        console.error('Fehler beim Kopieren: ', err);
      });
    };
    
    const confirmDeleteTest = (test) => {
      testToDelete.value = test;
      showDeleteModal();
    };
    
    const deleteTest = async () => {
      if (!testToDelete.value) return;
      
      try {
        await testsStore.deleteTest(testToDelete.value.id);
        toast.success('Test wurde erfolgreich gelöscht');
        loadTests(); // Liste neu laden
      } catch (err) {
        toast.error('Fehler beim Löschen des Tests');
        console.error(err);
      } finally {
        testToDelete.value = null;
      }
    };
    
    const saveTestsOrder = async () => {
      if (JSON.stringify(sortableTests.value) !== JSON.stringify(tests.value)) {
        try {
          const payload = sortableTests.value.map((t, index) => ({ id: t.id, sort_order: index }));
          await testsStore.reorderTests(payload);
          await loadTests();
          toast.success('Reihenfolge der Tests wurde aktualisiert');
        } catch (err) {
          toast.error('Fehler beim Speichern der Reihenfolge');
          console.error(err);
        }
      }
    };
    
    const getCompletionsCount = (test) => {
      if (!test) return 0;
      return test.completions_count || 0;
    };
    
    const truncateDescription = (text, length = 100) => {
      if (!text) return '';
      if (text.length <= length) return text;
      
      return text.substring(0, length) + '...';
    };
    
    const formatDuration = (minutes) => {
      if (!minutes || isNaN(minutes)) return '—';
      
      if (minutes < 60) {
        return `${minutes} Min.`;
      } else {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours} Std.${mins > 0 ? ` ${mins} Min.` : ''}`;
      }
    };
    
    const getCategoryClass = (category) => {
      if (!category) return 'bg-secondary';
      
      // Simplified category to class mapping
      const classMap = {
        'technical': 'bg-primary',
        'coding': 'bg-info',
        'design': 'bg-success',
        'logic': 'bg-warning',
        'personality': 'bg-danger'
      };
      
      return classMap[category.toLowerCase()] || 'bg-secondary';
    };
    
    const viewTest = (id) => {
      router.push(`/tests/${id}`);
    };
    
    const editTest = (id) => {
      router.push(`/tests/${id}/edit`);
    };
    
    const getQuestionCount = (test) => {
      if (!test) return 0;
      if (test.question_count_including_groups !== undefined) return test.question_count_including_groups;
      return test.question_count || 0;
    };
    
    const getTotalPoints = (test) => {
      if (!test) return 0;
      if (test.total_points_including_groups !== undefined) return test.total_points_including_groups;
      if (test.total_points !== undefined) return test.total_points;
      return 0; // Fallback
    };
    
    // Beim Montieren der Komponente
    onMounted(async () => {
      await loadTests();
      // Sofort sortableTests initialisieren
      updateSortableTests();
    });
    
    return {
      loading,
      error,
      searchQuery,
      currentFilter,
      testToDelete,
      currentTestUrl,
      currentTestHash,
      currentTestId,
      currentTestActive,
      sortableTests,
      deleteModal,
      shareModal,
      tests,
      filteredTests,
      loadTests,
      formatDate,
      showShareModal,
      showDeleteModal,
      shareTest,
      filterTests,
      copyTestLink,
      confirmDeleteTest,
      deleteTest,
      saveTestsOrder,
      getCompletionsCount,
      truncateDescription,
      formatDuration,
      getCategoryClass,
      viewTest,
      editTest,
      getQuestionCount,
      getTotalPoints,
      updateSortableTests
    };
  }
});
</script>

<style scoped>
.tests-container {
  --card-bg: #fff;
  --card-border: rgba(0, 0, 0, 0.1);
  --text-primary: #2c3e50;
  --text-secondary: #64748b;
  --hover-bg: #f8fafc;
  --drag-handle-color: #94a3b8;
  --badge-inactive-bg: #f1f5f9;
  --badge-inactive-color: #64748b;
  --divider-color: #e2e8f0;
  --icon-hover-bg: #f1f5f9;
  --btn-hover-shadow: rgba(0, 0, 0, 0.1);
  
  .dark-mode & {
    --card-bg: #1e293b;
    --card-border: rgba(255, 255, 255, 0.1);
    --text-primary: #e2e8f0;
    --text-secondary: #94a3b8;
    --hover-bg: #2c3e50;
    --drag-handle-color: #64748b;
    --badge-inactive-bg: #334155;
    --badge-inactive-color: #94a3b8;
    --divider-color: #334155;
    --icon-hover-bg: #334155;
    --btn-hover-shadow: rgba(0, 0, 0, 0.25);
  }
  
  .card-with-table {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    }
  }
  
  .card-list-item {
    padding: 1.5rem;
    border-bottom: 1px solid var(--divider-color);
    transition: all 0.2s ease;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background: var(--hover-bg);
    }
    
    &.card-list-item-inactive {
      opacity: 0.75;
    }
    
    .item-title {
      color: var(--text-primary);
      font-size: 1.1rem;
      margin-bottom: 0.5rem;
    }
    
    .item-description {
      color: var(--text-secondary);
      font-size: 0.9rem;
    }
    
    .item-meta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 0.75rem;
      
      .badge {
        padding: 0.35em 0.75em;
        font-size: 0.75rem;
        font-weight: 600;
        border-radius: 6px;
        
        &.bg-danger {
          background: var(--badge-inactive-bg) !important;
          color: var(--badge-inactive-color);
        }
      }
      
      .item-meta-stat {
        color: var(--text-secondary);
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        
        .icon {
          opacity: 0.7;
        }
      }
    }
  }
  
  .drag-handle {
    cursor: grab;
    color: var(--drag-handle-color);
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    
    &:hover {
      background: var(--icon-hover-bg);
    }
    
    &:active {
      cursor: grabbing;
    }
  }
  
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    
    .btn-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      color: var(--text-secondary);
      transition: all 0.2s ease;
      position: relative;
      
      &:hover {
        background: var(--icon-hover-bg);
        transform: translateY(-2px);
        box-shadow: 0 4px 6px var(--btn-hover-shadow);
      }
      
      &.btn-icon-primary:hover { color: var(--primary-color); }
      &.btn-icon-info:hover { color: var(--info-color); }
      &.btn-icon-secondary:hover { color: var(--secondary-color); }
      &.btn-icon-danger:hover { color: var(--danger-color); }
    }
  }
  
  .search-input-group {
    .input-group {
      background: var(--card-bg);
      border: 1px solid var(--card-border);
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.2s ease;
      
      &:focus-within {
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
      }
      
      .input-group-text {
        border: none;
        background: transparent;
        color: var(--text-secondary);
      }
      
      .form-control {
        border: none;
        background: transparent;
        color: var(--text-primary);
        padding: 0.75rem 1rem;
        
        &::placeholder {
          color: var(--text-secondary);
          opacity: 0.7;
        }
      }
    }
  }
  
  .draggable-ghost {
    background: var(--hover-bg);
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }
  
  .content-loading, .content-empty {
    padding: 3rem 1.5rem;
    text-align: center;
    
    .icon-xxl {
      font-size: 3rem;
      margin-bottom: 1.5rem;
      color: var(--text-secondary);
      opacity: 0.5;
    }
    
    .lead {
      color: var(--text-secondary);
    }
  }
  
  .display-heading {
    color: var(--text-primary);
    font-size: 2rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon-circle {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(var(--primary-color-rgb), 0.1);
      color: var(--primary-color);
    }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style> 